// src/Studio/masks/MaskEditorOverlay.tsx

import React, { useCallback, useMemo, useState } from 'react';
import { Group, Shape, Circle, Line } from 'react-konva';
import type Konva from 'konva';
import type { MaskPath, MaskPoint, VectorMask, MaskEditState, GradientFade } from '../types/gradientFade';
import { drawMaskPathToContext } from './maskPathUtils';
import { getGradientHandles } from './gradientRenderer';

interface MaskEditorOverlayProps {
  elementX: number;
  elementY: number;
  elementWidth: number;
  elementHeight: number;
  elementRotation?: number;
  mask: VectorMask;
  gradient?: GradientFade;
  editState: MaskEditState;
  activePath: MaskPath | null;
  onPointDragStart?: (pathId: string, pointIndex: number) => void;
  onPointDrag?: (pathId: string, pointIndex: number, x: number, y: number) => void;
  onPointDragEnd?: (pathId: string, pointIndex: number) => void;
  onHandleDrag?: (pathId: string, pointIndex: number, handleType: 'cpIn' | 'cpOut', x: number, y: number) => void;
  onGradientHandleDrag?: (handleType: 'start' | 'end' | 'center' | 'radius', x: number, y: number) => void;
  onPointDoubleClick?: (pathId: string, pointIndex: number) => void;
  showOverlayPreview?: boolean;
}

// Colors
const PATH_STROKE_COLOR = '#FBBF24'; // amber-400
const PATH_STROKE_ACTIVE = '#F59E0B'; // amber-500
const POINT_FILL = '#FFFFFF';
const POINT_STROKE = '#000000';
const HANDLE_FILL = '#60A5FA'; // blue-400
const HANDLE_LINE = '#60A5FA';
const GRADIENT_HANDLE_FILL = '#34D399'; // emerald-400
const GRADIENT_HANDLE_HOVER = '#10B981'; // emerald-500 (brighter on hover)
const DIM_OVERLAY_COLOR = 'rgba(0, 0, 0, 0.5)';
const MASK_PREVIEW_COLOR = 'rgba(239, 68, 68, 0.4)'; // red-500

// Helper to change cursor style
const setCursor = (cursor: string) => {
  document.body.style.cursor = cursor;
};

export function MaskEditorOverlay({
  elementX,
  elementY,
  elementWidth,
  elementHeight,
  elementRotation = 0,
  mask,
  gradient,
  editState,
  activePath,
  onPointDragStart,
  onPointDrag,
  onPointDragEnd,
  onHandleDrag,
  onGradientHandleDrag,
  onPointDoubleClick,
  showOverlayPreview = false,
}: MaskEditorOverlayProps) {
  // Track which gradient handle is being hovered
  const [hoveredHandle, setHoveredHandle] = useState<'start' | 'end' | 'center' | 'radius' | null>(null);

  // Combine committed paths with active (drawing) path
  const allPaths = useMemo(() => {
    const paths = [...mask.paths];
    if (activePath && activePath.points.length > 0) {
      paths.push(activePath);
    }
    return paths;
  }, [mask.paths, activePath]);

  // Render dimmed overlay (outside mask area)
  const renderDimOverlay = useCallback((ctx: Konva.Context, shape: Konva.Shape) => {
    const context = ctx._context;

    // Fill entire area with dim color
    context.fillStyle = showOverlayPreview ? MASK_PREVIEW_COLOR : DIM_OVERLAY_COLOR;
    context.fillRect(0, 0, elementWidth, elementHeight);

    // Cut out the mask areas (make them transparent)
    context.globalCompositeOperation = 'destination-out';
    context.fillStyle = 'white';

    for (const path of allPaths) {
      if (path.points.length >= 3 && path.closed) {
        drawMaskPathToContext(context, path);
        context.fill();
      }
    }

    context.globalCompositeOperation = 'source-over';
  }, [allPaths, elementWidth, elementHeight, showOverlayPreview]);

  // Render path strokes
  const renderPathStrokes = useMemo(() => {
    return allPaths.map((path) => {
      const isSelected = path.id === editState.selectedPathId;
      const isActive = path === activePath;

      return (
        <Shape
          key={path.id}
          sceneFunc={(ctx, shape) => {
            const context = ctx._context;

            if (path.points.length < 2) return;

            context.beginPath();
            path.points.forEach((pt, i) => {
              if (i === 0) {
                context.moveTo(pt.x, pt.y);
              } else {
                const prev = path.points[i - 1];
                if (prev.cpOut || pt.cpIn) {
                  const cp1 = prev.cpOut || { x: prev.x, y: prev.y };
                  const cp2 = pt.cpIn || { x: pt.x, y: pt.y };
                  context.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, pt.x, pt.y);
                } else {
                  context.lineTo(pt.x, pt.y);
                }
              }
            });

            if (path.closed) {
              context.closePath();
            }

            context.strokeStyle = isSelected || isActive ? PATH_STROKE_ACTIVE : PATH_STROKE_COLOR;
            context.lineWidth = 2;
            context.setLineDash(isActive && !path.closed ? [5, 5] : []);
            context.stroke();
          }}
          listening={false}
        />
      );
    });
  }, [allPaths, editState.selectedPathId, activePath]);

  // Render point handles (only for selected/active paths in pen mode)
  const renderPointHandles = useMemo(() => {
    const handles: React.ReactNode[] = [];

    allPaths.forEach((path) => {
      const isSelected = path.id === editState.selectedPathId || path === activePath;
      if (!isSelected || editState.tool !== 'pen') return;

      path.points.forEach((pt, i) => {
        const isSelectedPoint =
          path.id === editState.selectedPathId &&
          i === editState.selectedPointIndex;

        // Control point lines and handles
        if (pt.cpIn) {
          handles.push(
            <Line
              key={`${path.id}-${i}-cpIn-line`}
              points={[pt.cpIn.x, pt.cpIn.y, pt.x, pt.y]}
              stroke={HANDLE_LINE}
              strokeWidth={1}
              listening={false}
            />
          );
          handles.push(
            <Circle
              key={`${path.id}-${i}-cpIn`}
              x={pt.cpIn.x}
              y={pt.cpIn.y}
              radius={4}
              fill={HANDLE_FILL}
              stroke={POINT_STROKE}
              strokeWidth={1}
              draggable
              onDragMove={(e) => {
                const pos = e.target.position();
                onHandleDrag?.(path.id, i, 'cpIn', pos.x, pos.y);
              }}
            />
          );
        }

        if (pt.cpOut) {
          handles.push(
            <Line
              key={`${path.id}-${i}-cpOut-line`}
              points={[pt.x, pt.y, pt.cpOut.x, pt.cpOut.y]}
              stroke={HANDLE_LINE}
              strokeWidth={1}
              listening={false}
            />
          );
          handles.push(
            <Circle
              key={`${path.id}-${i}-cpOut`}
              x={pt.cpOut.x}
              y={pt.cpOut.y}
              radius={4}
              fill={HANDLE_FILL}
              stroke={POINT_STROKE}
              strokeWidth={1}
              draggable
              onDragMove={(e) => {
                const pos = e.target.position();
                onHandleDrag?.(path.id, i, 'cpOut', pos.x, pos.y);
              }}
            />
          );
        }

        // Main point handle
        handles.push(
          <Circle
            key={`${path.id}-${i}`}
            x={pt.x}
            y={pt.y}
            radius={isSelectedPoint ? 7 : 5}
            fill={isSelectedPoint ? PATH_STROKE_ACTIVE : POINT_FILL}
            stroke={POINT_STROKE}
            strokeWidth={1.5}
            draggable
            onDragStart={() => onPointDragStart?.(path.id, i)}
            onDragMove={(e) => {
              const pos = e.target.position();
              onPointDrag?.(path.id, i, pos.x, pos.y);
            }}
            onDragEnd={() => onPointDragEnd?.(path.id, i)}
            onDblClick={() => onPointDoubleClick?.(path.id, i)}
          />
        );
      });
    });

    return handles;
  }, [allPaths, editState, activePath, onPointDragStart, onPointDrag, onPointDragEnd, onHandleDrag, onPointDoubleClick]);

  // Render gradient handles
  // Render gradient handles with hover effects and cursor changes
  const renderGradientHandles = useMemo(() => {
    if (!gradient) return null;

    const handles = getGradientHandles(gradient, elementWidth, elementHeight);
    const elements: React.ReactNode[] = [];

    // Common hover handlers for cursor change
    const handleMouseEnter = (handleType: 'start' | 'end' | 'center' | 'radius') => {
      setCursor('grab');
      setHoveredHandle(handleType);
    };
    const handleMouseLeave = () => {
      setCursor('default');
      setHoveredHandle(null);
    };
    const handleDragStart = () => {
      setCursor('grabbing');
    };
    const handleDragEnd = () => {
      setCursor('grab');
    };

    if (gradient.type === 'linear' && handles.start && handles.end) {
      // Line between handles - position derived from gradient state
      elements.push(
        <Line
          key={`gradient-line-${handles.start.x.toFixed(1)}-${handles.start.y.toFixed(1)}-${handles.end.x.toFixed(1)}-${handles.end.y.toFixed(1)}`}
          points={[handles.start.x, handles.start.y, handles.end.x, handles.end.y]}
          stroke={GRADIENT_HANDLE_FILL}
          strokeWidth={2}
          dash={[4, 4]}
          listening={false}
        />
      );

      // Start handle - with hover effect
      const isStartHovered = hoveredHandle === 'start';
      elements.push(
        <Circle
          key={`gradient-start-${handles.start.x.toFixed(1)}-${handles.start.y.toFixed(1)}`}
          x={handles.start.x}
          y={handles.start.y}
          radius={isStartHovered ? 10 : 8}
          fill={isStartHovered ? GRADIENT_HANDLE_HOVER : GRADIENT_HANDLE_FILL}
          stroke="#FFFFFF"
          strokeWidth={isStartHovered ? 3 : 2}
          draggable
          listening
          onMouseDown={(e) => { e.cancelBubble = true; }} // Stop propagation to prevent mask drawing
          onMouseEnter={() => handleMouseEnter('start')}
          onMouseLeave={handleMouseLeave}
          onDragStart={(e) => { e.cancelBubble = true; handleDragStart(); }}
          onDragEnd={handleDragEnd}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const pos = e.target.position();
            onGradientHandleDrag?.('start', pos.x, pos.y);
          }}
        />
      );

      // End handle - with hover effect
      const isEndHovered = hoveredHandle === 'end';
      elements.push(
        <Circle
          key={`gradient-end-${handles.end.x.toFixed(1)}-${handles.end.y.toFixed(1)}`}
          x={handles.end.x}
          y={handles.end.y}
          radius={isEndHovered ? 10 : 8}
          fill={isEndHovered ? GRADIENT_HANDLE_HOVER : GRADIENT_HANDLE_FILL}
          stroke="#FFFFFF"
          strokeWidth={isEndHovered ? 3 : 2}
          draggable
          listening
          onMouseDown={(e) => { e.cancelBubble = true; }} // Stop propagation to prevent mask drawing
          onMouseEnter={() => handleMouseEnter('end')}
          onMouseLeave={handleMouseLeave}
          onDragStart={(e) => { e.cancelBubble = true; handleDragStart(); }}
          onDragEnd={handleDragEnd}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const pos = e.target.position();
            onGradientHandleDrag?.('end', pos.x, pos.y);
          }}
        />
      );
    } else if (gradient.type === 'radial' && handles.center && handles.radius) {
      const r = Math.sqrt(
        (handles.radius.x - handles.center.x) ** 2 +
        (handles.radius.y - handles.center.y) ** 2
      );

      // Circle outline - position derived from gradient state
      elements.push(
        <Circle
          key={`gradient-circle-${handles.center.x.toFixed(1)}-${handles.center.y.toFixed(1)}-${r.toFixed(1)}`}
          x={handles.center.x}
          y={handles.center.y}
          radius={r}
          stroke={GRADIENT_HANDLE_FILL}
          strokeWidth={2}
          dash={[4, 4]}
          listening={false}
        />
      );

      // Center handle - with hover effect
      const isCenterHovered = hoveredHandle === 'center';
      elements.push(
        <Circle
          key={`gradient-center-${handles.center.x.toFixed(1)}-${handles.center.y.toFixed(1)}`}
          x={handles.center.x}
          y={handles.center.y}
          radius={isCenterHovered ? 10 : 8}
          fill={isCenterHovered ? GRADIENT_HANDLE_HOVER : GRADIENT_HANDLE_FILL}
          stroke="#FFFFFF"
          strokeWidth={isCenterHovered ? 3 : 2}
          draggable
          listening
          onMouseDown={(e) => { e.cancelBubble = true; }} // Stop propagation to prevent mask drawing
          onMouseEnter={() => handleMouseEnter('center')}
          onMouseLeave={handleMouseLeave}
          onDragStart={(e) => { e.cancelBubble = true; handleDragStart(); }}
          onDragEnd={handleDragEnd}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const pos = e.target.position();
            onGradientHandleDrag?.('center', pos.x, pos.y);
          }}
        />
      );

      // Radius handle - with hover effect
      const isRadiusHovered = hoveredHandle === 'radius';
      elements.push(
        <Circle
          key={`gradient-radius-${handles.radius.x.toFixed(1)}-${handles.radius.y.toFixed(1)}`}
          x={handles.radius.x}
          y={handles.radius.y}
          radius={isRadiusHovered ? 8 : 6}
          fill={isRadiusHovered ? GRADIENT_HANDLE_HOVER : GRADIENT_HANDLE_FILL}
          stroke="#FFFFFF"
          strokeWidth={isRadiusHovered ? 3 : 2}
          draggable
          listening
          onMouseDown={(e) => { e.cancelBubble = true; }} // Stop propagation to prevent mask drawing
          onMouseEnter={() => handleMouseEnter('radius')}
          onMouseLeave={handleMouseLeave}
          onDragStart={(e) => { e.cancelBubble = true; handleDragStart(); }}
          onDragEnd={handleDragEnd}
          onDragMove={(e) => {
            e.cancelBubble = true;
            const pos = e.target.position();
            onGradientHandleDrag?.('radius', pos.x, pos.y);
          }}
        />
      );
    }

    return elements;
  }, [gradient, elementWidth, elementHeight, onGradientHandleDrag, hoveredHandle, setHoveredHandle]);

  return (
    <>
      {/* Clipped group for overlay and path visuals */}
      <Group
        x={elementX}
        y={elementY}
        rotation={elementRotation}
        clipFunc={(ctx) => {
          ctx.rect(0, 0, elementWidth, elementHeight);
        }}
      >
        {/* Dimmed overlay */}
        <Shape sceneFunc={renderDimOverlay} listening={false} />

        {/* Path strokes */}
        {renderPathStrokes}
      </Group>

      {/* Unclipped group for interactive handles - full interaction area */}
      <Group
        x={elementX}
        y={elementY}
        rotation={elementRotation}
      >
        {/* Point handles */}
        {renderPointHandles}

        {/* Gradient handles - need full interaction without clipping */}
        {renderGradientHandles}
      </Group>
    </>
  );
}
